// src/api/supabase/viajeroEncomienda.js
//
// ── Design decisions ──────────────────────────────────────────────────────────
// Real Supabase adapter for Viajero Encomienda (viajes → bultos → paquetes →
// items). Drop-in replacement for src/api/mock/viajeroEncomienda.js: every
// exported name, parameter shape and return shape is identical, so the UI
// components never learn that the backend changed.
//
// Contract notes carried over from the mock, verbatim on purpose:
//   · Functions THROW on failure and return bare values — they do NOT return
//     { data, error }. The UI catches and surfaces err.message.
//   · All thrown messages are user-facing Spanish.
//   · validarCierre(viaje) is SYNC + pure (no network) so the Manifiesto tab can
//     recompute bloqueos/advertencias on every render.
//
// Deviations from the mock, all forced by the live schema (VIAJERO-SCHEMA-V2):
//   · Ids are uuids, not 'pq-new-1' strings. The UI treats ids as opaque.
//   · customers.phone_number is NOT NULL: a blank phone is STORED as '' but
//     RETURNED as null, so the shape the UI sees still matches the mock.
//   · customers has no `municipio` column — it is synthesised as null in the
//     return shape and never written.
//   · exportXlsx() has no backend yet and remains a stub (see its comment).
//     getTaxConfig() reads the encomienda customs_config row. generarAnexoPdf() sí es real:
//     construye el input y renderiza los bytes vía renderAnexoPdf (sin DOM).
//
// tenant_id is NEVER sent on INSERT: every viajero_* table carries
// DEFAULT current_tenant_id() and RLS re-checks it. Same convention as
// Desarrollos and the V1 container.
//
// `customers` is the exception. It carries a permissive SELECT policy
// ("Public phone lookup own tenant") whose only predicate is
// `deleted_at IS NULL` — no tenant clause. Postgres ORs permissive policies, so
// an unfiltered read returns EVERY tenant's customers. listCustomers /
// searchCustomers therefore filter by tenant_id by hand, resolved once through
// the zero-arg current_tenant_id() RPC and memoised.

import { supabase } from '../../supabaseClient';
import { renderAnexoPdf } from '../../utils/generateAnexoPDF';
import { fetchCustomsConfigRow } from '../../utils/customsCalc';

// ── Config ────────────────────────────────────────────────────────────────────

const COLS_VIAJE = 'id, tenant_id, status, trip_reference, notes, created_at, updated_at, closed_at';
const COLS_BULTO = 'id, encomienda_id, label, peso_lb, dimensiones, sort_order, created_at';
const COLS_PAQUETE =
  'id, encomienda_id, customer_id, bulto_id, nombre, tipo_ingreso, peso_lb, notes, sort_order, created_at';
const COLS_ITEM =
  'id, package_id, descripcion, cantidad, unidad_medida, fob_unitario_usd, cip_unitario_usd, ' +
  'customs_catalog_id, manual_override, is_new, extraction_source, requires_review, sort_order, created_at';
// Lectura de items con el embed FK a customs_catalog: de ahí salen los campos
// derivados catalogo_descripcion / catalogo_unidad_medida (solo lectura, nunca
// se escriben — la FK ON DELETE SET NULL garantiza que el embed nunca cuelga).
const COLS_ITEM_CON_CATALOGO = `${COLS_ITEM}, customs_catalog ( descripcion, unidad_medida )`;
const COLS_CUSTOMER = 'id, full_name, phone_number, record_completeness, created_via, created_at';
const COLS_CATALOG = 'id, descripcion, unidad_medida, fob_usd, cip_usd';

// ── Internals ─────────────────────────────────────────────────────────────────

const LOG = '[ViajeroEncomiendaAPI]';

const logError = (...args) => console.error(LOG, ...args);

export const normalizar = (str) =>
  String(str ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// A paquete/item id the database has never seen (client-only row). Anything that
// is not a uuid is local — the mock's 'pq-new-1' and V1's 'tmp-…' both qualify.
const esIdLocal = (id) => !UUID_RE.test(String(id ?? ''));

const nowIso = () => new Date().toISOString();

// PostgREST hands `numeric` back as a JSON number, but a hand-written row or a
// future driver change could hand back a string. Coerce at the boundary so
// produccionCalc.js only ever sees numbers.
const num = (v, fallback = 0) => {
  if (v === null || v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Resolved once per page load. current_tenant_id() is a zero-arg public function,
// so PostgREST exposes it as an RPC — no React context needed in a plain module.
let tenantPromise = null;
async function tenantId() {
  if (!tenantPromise) {
    tenantPromise = (async () => {
      const { data, error } = await supabase.rpc('current_tenant_id');
      if (error) throw error;
      if (!data) throw new Error('No se pudo determinar el tenant actual.');
      return data;
    })().catch((err) => {
      tenantPromise = null; // let the next call retry instead of caching the failure
      throw err;
    });
  }
  return tenantPromise;
}

// Every public function funnels through here: one try/catch, one log site, one
// Spanish message. `mensaje` is what the UI shows when the driver error is noise.
async function guard(fnName, mensaje, run) {
  try {
    return await run();
  } catch (err) {
    logError(`${fnName} falló`, err);
    if (err?.code === '42501') {
      throw new Error('La base rechazó la operación (permisos de este tenant).');
    }
    if (err?.code === '23503') {
      throw new Error('Referencia inválida: el registro relacionado no existe en este tenant.');
    }
    throw new Error(err?.message || mensaje);
  }
}

// A DELETE blocked by RLS does NOT raise — it silently affects zero rows. Every
// delete goes through here so that case becomes a real error.
async function borrarOFallar(tabla, id, mensaje) {
  const { data, error } = await supabase.from(tabla).delete().eq('id', id).select('id');
  if (error) throw error;
  if (!data || data.length === 0) {
    logError(`el borrado en ${tabla} no afectó filas (RLS)`, id);
    throw new Error(mensaje);
  }
  return data;
}

// ── Mappers ───────────────────────────────────────────────────────────────────

const mapBulto = (row) => ({
  id: row.id,
  label: row.label ?? '',
  peso_lb: numOrNull(row.peso_lb),
  dimensiones: row.dimensiones ?? '',
  sort_order: num(row.sort_order),
});

const mapItem = (row) => ({
  id: row.id,
  descripcion: row.descripcion ?? '',
  cantidad: num(row.cantidad, 1),
  unidad_medida: row.unidad_medida ?? 'UNIDAD',
  fob_unitario_usd: num(row.fob_unitario_usd),
  cip_unitario_usd: numOrNull(row.cip_unitario_usd),
  customs_catalog_id: row.customs_catalog_id ?? null,
  catalogo_descripcion: row.customs_catalog?.descripcion ?? null,
  catalogo_unidad_medida: row.customs_catalog?.unidad_medida ?? null,
  manual_override: Boolean(row.manual_override),
  is_new: row.is_new === undefined || row.is_new === null ? true : Boolean(row.is_new),
  extraction_source: row.extraction_source ?? 'manual',
  requires_review: Boolean(row.requires_review),
  sort_order: num(row.sort_order),
  created_at: row.created_at ?? null,
});

const mapPaquete = (row, items = []) => ({
  id: row.id,
  bulto_id: row.bulto_id ?? null,
  customer_id: row.customer_id ?? null,
  nombre: row.nombre ?? '',
  tipo_ingreso: row.tipo_ingreso ?? null,
  sort_order: num(row.sort_order),
  created_at: row.created_at ?? null,
  items,
});

const mapCustomer = (row) => ({
  id: row.id,
  full_name: row.full_name ?? '',
  // '' is how a blank phone is stored (the column is NOT NULL); the mock's shape
  // is null, so normalise on the way out.
  phone_number: (row.phone_number ?? '') === '' ? null : row.phone_number,
  municipio: null, // no such column — synthesised to preserve the mock's shape
  record_completeness: row.record_completeness ?? 'full',
  created_via: row.created_via ?? 'full_form',
  created_at: row.created_at ?? null,
});

// ── Viajes ────────────────────────────────────────────────────────────────────

// Counts come from two grouped reads rather than a nested select, so a trip with
// no bultos/paquetes still lists (an inner join would hide it).
export async function listViajes() {
  return guard('listViajes', 'No se pudieron cargar los viajes.', async () => {
    const { data: viajes, error } = await supabase
      .from('viajero_encomiendas')
      .select(COLS_VIAJE)
      .order('created_at', { ascending: false });
    if (error) throw error;

    const filas = viajes || [];
    const ids = filas.map((v) => v.id);
    if (ids.length === 0) return [];

    const [{ data: pkgs, error: errP }, { data: bls, error: errB }] = await Promise.all([
      supabase.from('viajero_encomienda_packages').select('id, encomienda_id').in('encomienda_id', ids),
      supabase.from('viajero_bultos').select('id, encomienda_id').in('encomienda_id', ids),
    ]);
    if (errP) throw errP;
    if (errB) throw errB;

    const contar = (rows) =>
      (rows || []).reduce((acc, r) => {
        acc[r.encomienda_id] = (acc[r.encomienda_id] || 0) + 1;
        return acc;
      }, {});
    const porPaquete = contar(pkgs);
    const porBulto = contar(bls);

    return filas.map((v) => ({
      id: v.id,
      trip_reference: v.trip_reference,
      status: v.status,
      created_at: v.created_at,
      updated_at: v.updated_at,
      total_paquetes: porPaquete[v.id] || 0,
      total_bultos: porBulto[v.id] || 0,
    }));
  });
}

// Three reads + grouping in JS, matching the V1 container: the UI consumes a
// nested viaje (bultos[], paquetes[].items[]).
export async function getViaje(id) {
  return guard('getViaje', 'No se pudo abrir el viaje.', async () => {
    const { data: cab, error: errCab } = await supabase
      .from('viajero_encomiendas')
      .select(COLS_VIAJE)
      .eq('id', id)
      .maybeSingle();
    if (errCab) throw errCab;
    if (!cab) throw new Error('Viaje no encontrado');

    const [{ data: bultos, error: errB }, { data: pkgs, error: errP }] = await Promise.all([
      supabase
        .from('viajero_bultos')
        .select(COLS_BULTO)
        .eq('encomienda_id', id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('viajero_encomienda_packages')
        .select(COLS_PAQUETE)
        .eq('encomienda_id', id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
    ]);
    if (errB) throw errB;
    if (errP) throw errP;

    const filasPkg = pkgs || [];
    const idsPkg = filasPkg.map((p) => p.id);
    let filasItem = [];
    if (idsPkg.length > 0) {
      const { data: its, error: errI } = await supabase
        .from('viajero_encomienda_items')
        .select(COLS_ITEM_CON_CATALOGO)
        .in('package_id', idsPkg)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (errI) throw errI;
      filasItem = its || [];
    }

    const porPaquete = {};
    filasItem.forEach((it) => {
      (porPaquete[it.package_id] = porPaquete[it.package_id] || []).push(mapItem(it));
    });

    return {
      id: cab.id,
      tenant_id: cab.tenant_id,
      trip_reference: cab.trip_reference,
      status: cab.status,
      created_at: cab.created_at,
      updated_at: cab.updated_at,
      closed_at: cab.closed_at ?? null,
      bultos: (bultos || []).map(mapBulto),
      paquetes: filasPkg.map((p) => mapPaquete(p, porPaquete[p.id] || [])),
    };
  });
}

export async function createViaje({ trip_reference } = {}) {
  return guard('createViaje', 'No se pudo crear el viaje.', async () => {
    const { data, error } = await supabase
      .from('viajero_encomiendas')
      .insert([{ trip_reference: (trip_reference || '').trim() || 'Nuevo viaje' }])
      .select(COLS_VIAJE)
      .single();
    if (error) throw error;
    return {
      id: data.id,
      tenant_id: data.tenant_id,
      trip_reference: data.trip_reference,
      status: data.status,
      created_at: data.created_at,
      updated_at: data.updated_at,
      closed_at: data.closed_at ?? null,
      bultos: [],
      paquetes: [],
    };
  });
}

export async function updateViaje(id, { trip_reference } = {}) {
  return guard('updateViaje', 'No se pudo actualizar el viaje.', async () => {
    if (trip_reference !== undefined) {
      const { error } = await supabase
        .from('viajero_encomiendas')
        .update({ trip_reference })
        .eq('id', id);
      if (error) throw error;
    }
    return getViaje(id);
  });
}

// Autosave carries FIELD EDITS ONLY. Structural changes (add/remove bulto,
// paquete, item) already hit the API the moment they happen — same split as V1 —
// so this never inserts or deletes, it only writes back values on rows that
// already exist. Client-only ids are skipped; they belong to a structural call
// that has not resolved yet.
//
// One upsert per table instead of one UPDATE per row: a 9-paquete trip would
// otherwise fire ~50 requests every 3 s.
export async function autosaveViaje(id, { trip_reference, bultos, paquetes } = {}) {
  return guard('autosaveViaje', 'No se pudo guardar el viaje.', async () => {
    if (trip_reference !== undefined) {
      const { error } = await supabase
        .from('viajero_encomiendas')
        .update({ trip_reference })
        .eq('id', id);
      if (error) throw error;
    }

    if (bultos !== undefined) {
      const filas = (bultos || [])
        .filter((b) => b && !esIdLocal(b.id))
        .map((b) => ({
          id: b.id,
          encomienda_id: id,
          label: (b.label || '').trim() || 'Bulto',
          peso_lb: numOrNull(b.peso_lb),
          dimensiones: b.dimensiones ?? null,
          sort_order: num(b.sort_order),
        }));
      if (filas.length > 0) {
        const { error } = await supabase.from('viajero_bultos').upsert(filas, { onConflict: 'id' });
        if (error) throw error;
      }
    }

    if (paquetes !== undefined) {
      const lista = (paquetes || []).filter((p) => p && !esIdLocal(p.id));

      const filasPkg = lista.map((p) => ({
        id: p.id,
        encomienda_id: id,
        customer_id: p.customer_id || null,
        bulto_id: p.bulto_id && !esIdLocal(p.bulto_id) ? p.bulto_id : null,
        nombre: (p.nombre || '').trim() || null,
        tipo_ingreso: p.tipo_ingreso || null,
        sort_order: num(p.sort_order),
      }));
      if (filasPkg.length > 0) {
        const { error } = await supabase
          .from('viajero_encomienda_packages')
          .upsert(filasPkg, { onConflict: 'id' });
        if (error) throw error;
      }

      const filasItem = lista.flatMap((p) =>
        (p.items || [])
          .filter((it) => it && !esIdLocal(it.id))
          .map((it) => ({
            id: it.id,
            package_id: p.id,
            descripcion: it.descripcion ?? '',
            cantidad: num(it.cantidad, 1),
            unidad_medida: it.unidad_medida || 'UNIDAD',
            fob_unitario_usd: num(it.fob_unitario_usd),
            cip_unitario_usd: numOrNull(it.cip_unitario_usd),
            customs_catalog_id: it.customs_catalog_id || null,
            manual_override: Boolean(it.manual_override),
            is_new: it.is_new === undefined ? true : Boolean(it.is_new),
            extraction_source: it.extraction_source || 'manual',
            requires_review: Boolean(it.requires_review),
            sort_order: num(it.sort_order),
          })),
      );
      if (filasItem.length > 0) {
        const { error } = await supabase
          .from('viajero_encomienda_items')
          .upsert(filasItem, { onConflict: 'id' });
        if (error) throw error;
      }
    }

    return getViaje(id);
  });
}

// ── Bultos ────────────────────────────────────────────────────────────────────

export async function addBulto(viajeId, { label, peso_lb, dimensiones } = {}) {
  return guard('addBulto', 'No se pudo crear el bulto.', async () => {
    const { count, error: errCount } = await supabase
      .from('viajero_bultos')
      .select('id', { count: 'exact', head: true })
      .eq('encomienda_id', viajeId);
    if (errCount) throw errCount;
    const siguiente = (count || 0) + 1;

    const { data, error } = await supabase
      .from('viajero_bultos')
      .insert([
        {
          encomienda_id: viajeId,
          label: label || `Bulto ${siguiente}`,
          peso_lb: numOrNull(peso_lb),
          dimensiones: dimensiones ?? null,
          sort_order: siguiente,
        },
      ])
      .select(COLS_BULTO)
      .single();
    if (error) throw error;
    return mapBulto(data);
  });
}

export async function updateBulto(viajeId, bultoId, patch = {}) {
  return guard('updateBulto', 'No se pudo actualizar el bulto.', async () => {
    const campos = {};
    if (patch.label !== undefined) campos.label = (patch.label || '').trim() || 'Bulto';
    if (patch.peso_lb !== undefined) campos.peso_lb = numOrNull(patch.peso_lb);
    if (patch.dimensiones !== undefined) campos.dimensiones = patch.dimensiones ?? null;
    if (patch.sort_order !== undefined) campos.sort_order = num(patch.sort_order);

    if (Object.keys(campos).length === 0) {
      const { data, error } = await supabase
        .from('viajero_bultos')
        .select(COLS_BULTO)
        .eq('id', bultoId)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Bulto no encontrado');
      return mapBulto(data);
    }

    const { data, error } = await supabase
      .from('viajero_bultos')
      .update(campos)
      .eq('id', bultoId)
      .select(COLS_BULTO)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Bulto no encontrado');
    return mapBulto(data);
  });
}

// Mirrors the mock: dropping a bulto orphans its paquetes rather than cascading.
export async function removeBulto(viajeId, bultoId) {
  return guard('removeBulto', 'No se pudo quitar el bulto.', async () => {
    const { error: errDetach } = await supabase
      .from('viajero_encomienda_packages')
      .update({ bulto_id: null })
      .eq('encomienda_id', viajeId)
      .eq('bulto_id', bultoId);
    if (errDetach) throw errDetach;

    await borrarOFallar(
      'viajero_bultos',
      bultoId,
      'No se pudo quitar el bulto: se requiere rol admin o manager.',
    );
    return getViaje(viajeId);
  });
}

// ── Paquetes ──────────────────────────────────────────────────────────────────

export async function addPaquete(viajeId, { customer_id, nombre, bulto_id, tipo_ingreso } = {}) {
  return guard('addPaquete', 'No se pudo crear el paquete.', async () => {
    const { count, error: errCount } = await supabase
      .from('viajero_encomienda_packages')
      .select('id', { count: 'exact', head: true })
      .eq('encomienda_id', viajeId);
    if (errCount) throw errCount;
    const siguiente = (count || 0) + 1;

    let etiqueta = nombre;
    if (etiqueta === undefined || etiqueta === null) {
      if (customer_id) {
        const { data: cli } = await supabase
          .from('customers')
          .select('full_name')
          .eq('id', customer_id)
          .maybeSingle();
        etiqueta = cli?.full_name || `Paquete ${siguiente}`;
      } else {
        etiqueta = `Paquete ${siguiente}`;
      }
    }

    const { data, error } = await supabase
      .from('viajero_encomienda_packages')
      .insert([
        {
          encomienda_id: viajeId,
          customer_id: customer_id ?? null,
          bulto_id: bulto_id ?? null,
          nombre: etiqueta,
          tipo_ingreso: tipo_ingreso ?? null,
          sort_order: siguiente,
        },
      ])
      .select(COLS_PAQUETE)
      .single();
    if (error) throw error;
    return mapPaquete(data, []);
  });
}

export async function updatePaquete(viajeId, paqueteId, patch = {}) {
  return guard('updatePaquete', 'No se pudo actualizar el paquete.', async () => {
    // `items` is state the UI owns; it is never written from here (item rows are
    // maintained by addItem/updateItem/removeItem + autosave).
    const campos = {};
    if (patch.customer_id !== undefined) campos.customer_id = patch.customer_id || null;
    if (patch.bulto_id !== undefined) campos.bulto_id = patch.bulto_id || null;
    if (patch.nombre !== undefined) campos.nombre = (patch.nombre || '').trim() || null;
    if (patch.tipo_ingreso !== undefined) campos.tipo_ingreso = patch.tipo_ingreso || null;
    if (patch.sort_order !== undefined) campos.sort_order = num(patch.sort_order);

    let fila;
    if (Object.keys(campos).length === 0) {
      const { data, error } = await supabase
        .from('viajero_encomienda_packages')
        .select(COLS_PAQUETE)
        .eq('id', paqueteId)
        .maybeSingle();
      if (error) throw error;
      fila = data;
    } else {
      const { data, error } = await supabase
        .from('viajero_encomienda_packages')
        .update(campos)
        .eq('id', paqueteId)
        .select(COLS_PAQUETE)
        .maybeSingle();
      if (error) throw error;
      fila = data;
    }
    if (!fila) throw new Error('Paquete no encontrado');

    const { data: its, error: errI } = await supabase
      .from('viajero_encomienda_items')
      .select(COLS_ITEM_CON_CATALOGO)
      .eq('package_id', paqueteId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (errI) throw errI;

    return mapPaquete(fila, (its || []).map(mapItem));
  });
}

export async function removePaquete(viajeId, paqueteId) {
  return guard('removePaquete', 'No se pudo quitar el paquete.', async () => {
    // Items first: the FK is NOT NULL, so a surviving child would block the
    // parent delete on databases without ON DELETE CASCADE.
    const { error: errItems } = await supabase
      .from('viajero_encomienda_items')
      .delete()
      .eq('package_id', paqueteId);
    if (errItems) throw errItems;

    await borrarOFallar(
      'viajero_encomienda_packages',
      paqueteId,
      'No se pudo quitar el paquete: se requiere rol admin o manager.',
    );
    return getViaje(viajeId);
  });
}

// package_id is NOT NULL, so the paquete must exist in the database before an
// item can reference it. If the UI still holds a client-only id, persist the
// paquete here and hand back the real uuid — same bootstrap as V1's
// asegurarPaquete.
async function asegurarPaquete(viajeId, paqueteId) {
  if (!esIdLocal(paqueteId)) return paqueteId;
  logError('paquete sin persistir; se crea antes de insertar el artículo', paqueteId);
  const creado = await addPaquete(viajeId, {});
  return creado.id;
}

// ── Items ─────────────────────────────────────────────────────────────────────

export async function addItem(viajeId, paqueteId, fields = {}) {
  return guard('addItem', 'No se pudo agregar el artículo.', async () => {
    const destino = await asegurarPaquete(viajeId, paqueteId);

    const { count, error: errCount } = await supabase
      .from('viajero_encomienda_items')
      .select('id', { count: 'exact', head: true })
      .eq('package_id', destino);
    if (errCount) throw errCount;

    const fila = {
      package_id: destino,
      descripcion: fields.descripcion ?? '',
      cantidad: num(fields.cantidad, 1),
      unidad_medida: fields.unidad_medida || 'UNIDAD',
      fob_unitario_usd: num(fields.fob_unitario_usd),
      cip_unitario_usd: numOrNull(fields.cip_unitario_usd),
      customs_catalog_id: fields.customs_catalog_id || null,
      manual_override: fields.manual_override === undefined ? true : Boolean(fields.manual_override),
      is_new: fields.is_new === undefined ? true : Boolean(fields.is_new),
      extraction_source: fields.extraction_source || 'manual',
      requires_review: Boolean(fields.requires_review),
      sort_order: fields.sort_order === undefined ? (count || 0) + 1 : num(fields.sort_order),
    };

    const { data, error } = await supabase
      .from('viajero_encomienda_items')
      .insert([fila])
      .select(COLS_ITEM_CON_CATALOGO)
      .single();
    if (error) throw error;
    return mapItem(data);
  });
}

export async function updateItem(viajeId, itemId, patch = {}) {
  return guard('updateItem', 'No se pudo actualizar el artículo.', async () => {
    const campos = {};
    if (patch.descripcion !== undefined) campos.descripcion = patch.descripcion ?? '';
    if (patch.cantidad !== undefined) campos.cantidad = num(patch.cantidad, 1);
    if (patch.unidad_medida !== undefined) campos.unidad_medida = patch.unidad_medida || 'UNIDAD';
    if (patch.fob_unitario_usd !== undefined) campos.fob_unitario_usd = num(patch.fob_unitario_usd);
    if (patch.cip_unitario_usd !== undefined) campos.cip_unitario_usd = numOrNull(patch.cip_unitario_usd);
    if (patch.customs_catalog_id !== undefined) campos.customs_catalog_id = patch.customs_catalog_id || null;
    if (patch.manual_override !== undefined) campos.manual_override = Boolean(patch.manual_override);
    if (patch.is_new !== undefined) campos.is_new = Boolean(patch.is_new);
    if (patch.extraction_source !== undefined) campos.extraction_source = patch.extraction_source || 'manual';
    if (patch.requires_review !== undefined) campos.requires_review = Boolean(patch.requires_review);
    if (patch.sort_order !== undefined) campos.sort_order = num(patch.sort_order);

    if (Object.keys(campos).length === 0) {
      const { data, error } = await supabase
        .from('viajero_encomienda_items')
        .select(COLS_ITEM_CON_CATALOGO)
        .eq('id', itemId)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Artículo no encontrado');
      return mapItem(data);
    }

    const { data, error } = await supabase
      .from('viajero_encomienda_items')
      .update(campos)
      .eq('id', itemId)
      .select(COLS_ITEM_CON_CATALOGO)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Artículo no encontrado');
    return mapItem(data);
  });
}

export async function removeItem(viajeId, itemId) {
  return guard('removeItem', 'No se pudo eliminar el artículo.', async () => {
    await borrarOFallar(
      'viajero_encomienda_items',
      itemId,
      'No se pudo eliminar el artículo: se requiere rol admin o manager.',
    );
    return getViaje(viajeId);
  });
}

// ── Customers ─────────────────────────────────────────────────────────────────

// The tenant filter is deliberate, not redundant: see the module header — the
// permissive SELECT policy on `customers` has no tenant predicate.
export async function listCustomers() {
  return guard('listCustomers', 'No se pudieron cargar los clientes.', async () => {
    const tid = await tenantId();
    const { data, error } = await supabase
      .from('customers')
      .select(COLS_CUSTOMER)
      .eq('tenant_id', tid)
      .is('deleted_at', null)
      .order('full_name', { ascending: true });
    if (error) throw error;
    return (data || []).map(mapCustomer);
  });
}

// Name matches rank above phone matches, max 8, empty query → first 8 —
// the mock's ordering contract. Filtering happens in JS because the ranking is
// two-pass and accent-insensitive.
export async function searchCustomers(q) {
  return guard('searchCustomers', 'No se pudieron buscar los clientes.', async () => {
    const todos = await listCustomers();
    const term = normalizar(q);
    if (!term) return todos.slice(0, 8);

    const digits = term.replace(/\D/g, '');
    const byName = todos.filter((c) => normalizar(c.full_name).includes(term));
    const byPhone =
      digits.length >= 3
        ? todos.filter(
            (c) =>
              !byName.includes(c) &&
              String(c.phone_number ?? '').replace(/\D/g, '').includes(digits),
          )
        : [];
    return [...byName, ...byPhone].slice(0, 8);
  });
}

function initialsFor(fullName) {
  const letters = normalizar(fullName)
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .filter((ch) => /[a-z]/.test(ch))
    .slice(0, 3)
    .join('')
    .toUpperCase();
  return letters || 'CL';
}

// customers_pkey is PRIMARY KEY (id) — ONE GLOBAL NAMESPACE, not (tenant_id, id).
// The collision scan is therefore deliberately UNSCOPED: a tenant-scoped scan
// would hand back ids another tenant already owns (miltonsv 'AR' vs maxienvios
// 'AR') and fail on 23505. The permissive SELECT policy makes the global read
// possible; the 23505 retry below closes the race between scan and insert.
async function idsTomados(base) {
  const { data, error } = await supabase
    .from('customers')
    .select('id')
    .ilike('id', `${base}%`);
  if (error) throw error;
  return new Set((data || []).map((r) => r.id));
}

const siguienteId = (base, taken) => {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}${n}`)) n += 1;
  return `${base}${n}`;
};

export async function quickCreateCustomer({ full_name, phone_number } = {}) {
  return guard('quickCreateCustomer', 'No se pudo crear el cliente.', async () => {
    const nombre = String(full_name ?? '').trim();
    if (!nombre) throw new Error('Ingresá el nombre del cliente.');

    const base = initialsFor(nombre);
    const taken = await idsTomados(base);
    // phone_number is NOT NULL: store '' for a blank, return null (mapCustomer).
    const telefono = String(phone_number ?? '').trim();

    for (let intento = 0; intento < 5; intento += 1) {
      const id = siguienteId(base, taken);
      const { data, error } = await supabase
        .from('customers')
        .insert([
          {
            id,
            full_name: nombre,
            phone_number: telefono,
            record_completeness: 'quick',
            created_via: 'quick_create',
          },
        ])
        .select(COLS_CUSTOMER)
        .single();

      if (!error) return mapCustomer(data);
      if (error.code !== '23505') throw error;
      // Someone claimed it between the scan and the insert — remember and retry.
      logError('colisión de id de cliente, reintentando', id);
      taken.add(id);
    }
    throw new Error('No se pudo asignar un ID para el cliente. Intentá de nuevo.');
  });
}

// ── Perfil aduanero ───────────────────────────────────────────────────────────

const COLS_PERFIL =
  'id, viaje_id, codigo_gestor, codigo_aduana, nombre_gestor, no_registro, estado, created_at, updated_at';

// Nunca null: la UI de carga y el config del Anexo leen campos sueltos, así que
// la ausencia de fila se devuelve como perfil vacío en borrador.
const PERFIL_VACIO = {
  id: null,
  viaje_id: null,
  codigo_gestor: '',
  codigo_aduana: '',
  nombre_gestor: '',
  no_registro: '',
  estado: 'borrador',
  created_at: null,
  updated_at: null,
};

const mapPerfil = (row) => ({
  id: row.id,
  viaje_id: row.viaje_id ?? null,
  codigo_gestor: row.codigo_gestor ?? '',
  codigo_aduana: row.codigo_aduana ?? '',
  nombre_gestor: row.nombre_gestor ?? '',
  no_registro: row.no_registro ?? '',
  estado: row.estado ?? 'borrador',
  created_at: row.created_at ?? null,
  updated_at: row.updated_at ?? null,
});

// Lecturas sin filtro de tenant: la política "staff read perfil" ya acota por
// current_tenant_id(). Con viajeId se prefiere el perfil de ese viaje y, si no
// hay, cae al perfil por defecto del tenant (viaje_id IS NULL).
export async function getPerfilAduanero(viajeId = null) {
  return guard('getPerfilAduanero', 'No se pudo cargar el perfil aduanero.', async () => {
    if (viajeId) {
      const { data, error } = await supabase
        .from('viajero_perfil_aduanero')
        .select(COLS_PERFIL)
        .eq('viaje_id', viajeId)
        .maybeSingle();
      if (error) throw error;
      if (data) return mapPerfil(data);
    }
    const { data, error } = await supabase
      .from('viajero_perfil_aduanero')
      .select(COLS_PERFIL)
      .is('viaje_id', null)
      .maybeSingle();
    if (error) throw error;
    return data ? mapPerfil(data) : { ...PERFIL_VACIO };
  });
}

// estado lo deriva SIEMPRE el guardado, nunca el llamador: 'activado' exige los
// dos códigos DGA presentes — un perfil sin gestor jamás debe imprimir Anexo.
const estadoPerfil = (v) => (v.codigo_gestor && v.codigo_aduana ? 'activado' : 'borrador');

// Upsert del perfil por defecto del tenant. El índice que respalda esa
// unicidad es PARCIAL (WHERE viaje_id IS NULL) y ON CONFLICT no puede inferir
// índices parciales vía PostgREST, así que el upsert va como
// select→update/insert con reintento en 23505 para cerrar la carrera.
// A diferencia del resto de tablas viajero_*, esta NO tiene DEFAULT
// current_tenant_id() ni trigger: tenant_id es NOT NULL y hay que enviarlo en
// el INSERT (la política de escritura solo lo verifica). viaje_id nunca se
// envía: queda NULL (el perfil por viaje es V2, fuera de alcance).
export async function savePerfilAduanero({
  codigo_gestor,
  codigo_aduana,
  nombre_gestor,
  no_registro,
} = {}) {
  return guard('savePerfilAduanero', 'No se pudo guardar el perfil aduanero.', async () => {
    const valores = {
      codigo_gestor: String(codigo_gestor ?? '').trim(),
      codigo_aduana: String(codigo_aduana ?? '').trim(),
      nombre_gestor: String(nombre_gestor ?? '').trim(),
      no_registro: String(no_registro ?? '').trim(),
    };
    const fila = { ...valores, estado: estadoPerfil(valores) };

    const actualizar = async (id) => {
      const { data, error } = await supabase
        .from('viajero_perfil_aduanero')
        .update({ ...fila, updated_at: nowIso() })
        .eq('id', id)
        .select(COLS_PERFIL)
        .single();
      if (error) throw error;
      return mapPerfil(data);
    };

    const { data: actual, error: errSel } = await supabase
      .from('viajero_perfil_aduanero')
      .select('id')
      .is('viaje_id', null)
      .maybeSingle();
    if (errSel) throw errSel;
    if (actual) return actualizar(actual.id);

    const { data, error } = await supabase
      .from('viajero_perfil_aduanero')
      .insert([{ ...fila, tenant_id: await tenantId() }])
      .select(COLS_PERFIL)
      .single();
    if (!error) return mapPerfil(data);
    if (error.code !== '23505') throw error;

    // Otro operador creó el perfil entre la lectura y el insert: actualizarlo.
    const { data: ganador, error: errRe } = await supabase
      .from('viajero_perfil_aduanero')
      .select('id')
      .is('viaje_id', null)
      .maybeSingle();
    if (errRe) throw errRe;
    if (!ganador) throw error;
    return actualizar(ganador.id);
  });
}

// ── Catalog ───────────────────────────────────────────────────────────────────

// EXACTLY top-3 max — the hover picker is a nudge, not a browser.
export async function searchCatalog(q) {
  return guard('searchCatalog', 'No se pudo buscar en el catálogo.', async () => {
    const term = normalizar(q);
    if (term.length < 3) return [];
    const { data, error } = await supabase
      .from('customs_catalog')
      .select(COLS_CATALOG)
      .ilike('descripcion', `%${term}%`)
      .limit(3);
    if (error) throw error;
    return (data || []).map((r) => ({
      id: r.id,
      descripcion: r.descripcion ?? '',
      unidad_medida: r.unidad_medida ?? 'UNIDAD',
      fob_usd: numOrNull(r.fob_usd),
      cip_usd: numOrNull(r.cip_usd),
    }));
  });
}

// ── AI extraction ─────────────────────────────────────────────────────────────

// Calls the deployed `encomienda-list-extract` Edge Function. Its response is
// already { extracted, requires_review, usage, model, cached, content_hash };
// the only normalisation needed is the per-item requires_review flag, which the
// function's prompt does not emit (it reports review at the top level only).
// tenant_id is what turns the function's content-hash cache on: without it the
// function behaves exactly as before (no lookup, no write, cached: false).
export async function extractLista({ image_base64, image_media_type, tenant_id } = {}) {
  return guard('extractLista', 'No se pudo leer la lista.', async () => {
    if (!image_base64) throw new Error('No se recibió ninguna imagen de la lista.');

    const { data, error } = await supabase.functions.invoke('encomienda-list-extract', {
      body: { image_base64, image_media_type, ...(tenant_id ? { tenant_id } : {}) },
    });
    if (error) throw error;
    if (!data) throw new Error('La extracción no devolvió resultados.');
    if (data.error) throw new Error(data.detail || data.error);

    const extracted = data.extracted || {};
    const paquetes = (extracted.paquetes || []).map((g) => ({
      owner_name_raw: g.owner_name_raw ?? '',
      items: (g.items || []).map((it) => ({
        description_raw: it.description_raw ?? '',
        qty: num(it.qty, 1),
        unit_type: it.unit_type || 'UNIDAD',
        unit_price: numOrNull(it.unit_price),
        is_new: it.is_new === undefined ? true : Boolean(it.is_new),
        notes: it.notes ?? null,
        // No per-item flag upstream: a null price is what the operator has to
        // resolve, so that is what gets flagged for review.
        requires_review:
          it.requires_review === undefined
            ? it.unit_price === null || it.unit_price === undefined
            : Boolean(it.requires_review),
      })),
    }));

    return {
      extracted: {
        paquetes,
        extraction_notes: extracted.extraction_notes ?? '',
        requires_review: Boolean(extracted.requires_review ?? data.requires_review),
      },
      requires_review: Boolean(data.requires_review),
      usage: { ...(data.usage || {}), media_type: image_media_type ?? null },
      model: data.model ?? null,
      cached: Boolean(data.cached),
      content_hash: data.content_hash ?? null,
    };
  });
}

// ── Extraction drafts ─────────────────────────────────────────────────────────
// One draft per trip (viajero_extraction_drafts PK = encomienda_id): saving
// again replaces the previous one. `payload` is the drawer's local state as-is;
// `source_meta` describes where it came from ({ source_tipo, image_media_type,
// content_hash, image_base64? }). tenant_id is never sent (DEFAULT
// current_tenant_id() + RLS), same as every other viajero_* table.

const COLS_DRAFT = 'encomienda_id, tenant_id, content_hash, source_meta, payload, created_at';

const mapDraft = (row) =>
  row
    ? {
        encomienda_id: row.encomienda_id,
        tenant_id: row.tenant_id ?? null,
        content_hash: row.content_hash ?? null,
        source_meta: row.source_meta ?? null,
        payload: row.payload ?? null,
        created_at: row.created_at ?? null,
      }
    : null;

export async function guardarBorradorExtraccion(encomiendaId, payload, sourceMeta = null) {
  return guard('guardarBorradorExtraccion', 'No se pudo guardar el borrador de la extracción.', async () => {
    if (!encomiendaId) throw new Error('Falta el viaje del borrador.');
    if (!payload) throw new Error('No hay nada que guardar en el borrador.');
    const { data, error } = await supabase
      .from('viajero_extraction_drafts')
      .upsert(
        [
          {
            encomienda_id: encomiendaId,
            content_hash: sourceMeta?.content_hash ?? null,
            source_meta: sourceMeta ?? null,
            payload,
            created_at: nowIso(), // "newer replaces older": the timestamp follows the latest save
          },
        ],
        { onConflict: 'encomienda_id' },
      )
      .select(COLS_DRAFT)
      .single();
    if (error) throw error;
    return mapDraft(data);
  });
}

export async function obtenerBorradorExtraccion(encomiendaId) {
  return guard('obtenerBorradorExtraccion', 'No se pudo leer el borrador de la extracción.', async () => {
    if (!encomiendaId) return null;
    const { data, error } = await supabase
      .from('viajero_extraction_drafts')
      .select(COLS_DRAFT)
      .eq('encomienda_id', encomiendaId)
      .maybeSingle();
    if (error) throw error;
    return mapDraft(data);
  });
}

// Deleting a draft that does not exist is not an error: Confirmar and Descartar
// both call this unconditionally.
export async function eliminarBorradorExtraccion(encomiendaId) {
  return guard('eliminarBorradorExtraccion', 'No se pudo eliminar el borrador de la extracción.', async () => {
    if (!encomiendaId) return false;
    const { data, error } = await supabase
      .from('viajero_extraction_drafts')
      .delete()
      .eq('encomienda_id', encomiendaId)
      .select('encomienda_id');
    if (error) throw error;
    return Boolean(data && data.length > 0);
  });
}

// Materialises confirmed extraction groups as paquetes. Items are flagged
// extraction_source 'ai' so the ProvenanceChip and review flow can tell them
// apart from hand-typed rows.
export async function confirmExtraction(viajeId, groups = []) {
  return guard('confirmExtraction', 'No se pudo confirmar la extracción.', async () => {
    const { count, error: errCount } = await supabase
      .from('viajero_encomienda_packages')
      .select('id', { count: 'exact', head: true })
      .eq('encomienda_id', viajeId);
    if (errCount) throw errCount;
    let siguiente = (count || 0) + 1;

    for (const g of groups || []) {
      let etiqueta = g.nombre;
      if (!etiqueta && g.customer_id) {
        const { data: cli } = await supabase
          .from('customers')
          .select('full_name')
          .eq('id', g.customer_id)
          .maybeSingle();
        etiqueta = cli?.full_name || null;
      }

      const { data: pkg, error: errPkg } = await supabase
        .from('viajero_encomienda_packages')
        .insert([
          {
            encomienda_id: viajeId,
            customer_id: g.customer_id ?? null,
            bulto_id: g.bulto_id ?? null,
            nombre: etiqueta || `Paquete ${siguiente}`,
            tipo_ingreso: g.tipo_ingreso ?? null,
            sort_order: siguiente,
          },
        ])
        .select('id')
        .single();
      if (errPkg) throw errPkg;
      siguiente += 1;

      // Rows the drawer locked to the catalog keep that link (customs_catalog_id
      // + cip, manual_override false); rows typed by hand in the drawer arrive
      // as extraction_source 'manual'. Everything else stays 'ai'.
      const filas = (g.items || []).map((it, idx) => ({
        package_id: pkg.id,
        descripcion: it.descripcion ?? '',
        cantidad: num(it.cantidad, 1),
        unidad_medida: it.unidad_medida || 'UNIDAD',
        fob_unitario_usd: num(it.fob_unitario_usd),
        cip_unitario_usd: it.customs_catalog_id ? numOrNull(it.cip_unitario_usd) : null,
        customs_catalog_id: it.customs_catalog_id || null,
        manual_override: !it.customs_catalog_id,
        is_new: it.is_new === undefined ? true : Boolean(it.is_new),
        extraction_source: it.extraction_source === 'manual' ? 'manual' : 'ai',
        requires_review: Boolean(it.requires_review),
        sort_order: idx + 1,
      }));
      if (filas.length > 0) {
        const { error: errItems } = await supabase.from('viajero_encomienda_items').insert(filas);
        if (errItems) throw errItems;
      }
    }

    return getViaje(viajeId);
  });
}

// ── Tax config ────────────────────────────────────────────────────────────────

// The encomienda customs_config row, as stored — no default rates. null means
// Producción shows no tax and says the config is missing.
export async function getTaxConfig() {
  return fetchCustomsConfigRow(supabase, 'encomienda');
}

// ── Close / reopen ────────────────────────────────────────────────────────────

// SYNC + pure, byte-for-byte the mock's rules. Bloqueos stop the close;
// advertencias are shown but allowed.
export function validarCierre(viaje) {
  const bloqueos = [];
  const advertencias = [];
  const paquetes = viaje?.paquetes ?? [];

  if (paquetes.length === 0) bloqueos.push('El viaje no tiene paquetes.');

  paquetes.forEach((p, idx) => {
    const nombre = p.nombre || `Paquete ${idx + 1}`;
    if (!p.customer_id) bloqueos.push(`El paquete «${nombre}» no tiene cliente asignado.`);
    if (!p.items || p.items.length === 0) bloqueos.push(`El paquete «${nombre}» no tiene artículos.`);
    if (!p.bulto_id) advertencias.push(`El paquete «${nombre}» no está asignado a ningún bulto.`);
    if (!p.tipo_ingreso) advertencias.push(`El paquete «${nombre}» no tiene tipo de ingreso.`);
    const pendientes = (p.items || []).filter((it) => it.requires_review).length;
    if (pendientes > 0) {
      advertencias.push(
        pendientes === 1
          ? `El paquete «${nombre}» tiene 1 artículo que requiere revisión.`
          : `El paquete «${nombre}» tiene ${pendientes} artículos que requieren revisión.`,
      );
    }
  });

  return { bloqueos, advertencias };
}

export async function cerrarManifiesto(viajeId) {
  return guard('cerrarManifiesto', 'No se pudo cerrar el manifiesto.', async () => {
    // Re-read before validating: the close decision must be made against what is
    // actually persisted, not against what the tab happens to be holding.
    const viaje = await getViaje(viajeId);
    const { bloqueos } = validarCierre(viaje);
    if (bloqueos.length > 0) throw new Error(bloqueos[0]);

    const { error } = await supabase
      .from('viajero_encomiendas')
      .update({ status: 'cerrado', closed_at: nowIso() })
      .eq('id', viajeId);
    if (error) throw error;
    return getViaje(viajeId);
  });
}

export async function reabrirManifiesto(viajeId) {
  return guard('reabrirManifiesto', 'No se pudo reabrir el manifiesto.', async () => {
    const { error } = await supabase
      .from('viajero_encomiendas')
      .update({ status: 'borrador', closed_at: null })
      .eq('id', viajeId);
    if (error) throw error;
    return getViaje(viajeId);
  });
}

// ── Exports ───────────────────────────────────────────────────────────────────

const slugRef = (ref) =>
  normalizar(ref).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'sin-referencia';

// STUB: no XLSX backend is wired yet. Returns the same synthetic descriptor the
// mock returned so the Producción tab's toast keeps working; it does NOT produce
// a file.
export async function exportXlsx(viajeId) {
  return guard('exportXlsx', 'No se pudo exportar el XLSX.', async () => {
    const viaje = await getViaje(viajeId);
    return { ok: true, filename: `viaje-${slugRef(viaje.trip_reference)}.xlsx` };
  });
}

// ── Anexo PDF ─────────────────────────────────────────────────────────────────
// El adaptador construye el input y produce los BYTES (sin DOM, sin estado de
// módulo — reentrante: una capa batch puede llamarlo N veces en paralelo). La
// descarga en el navegador vive en el componente.

const TASA_IMPUESTO_ANEXO = 0.145;

// Descripción efectiva de un artículo: la del catálogo cuando está ligado
// (guardia explícita sobre customs_catalog_id, sin confiar en el invariante
// de que catalogo_descripcion sea null al desligar), el texto crudo si no.
// El Anexo y el gate de completitud DEBEN leer la misma — nunca disentir.
export const descripcionEfectiva = (it) =>
  it?.customs_catalog_id ? it?.catalogo_descripcion ?? it?.descripcion ?? '' : it?.descripcion ?? '';

// Una fila es imprimible solo con descripción, cantidad y FOB presentes.
// Espejo del itemIncompleto de Desarrollos, sobre los nombres de campo viajero.
export const itemIncompleto = (it) =>
  !String(descripcionEfectiva(it)).trim() ||
  !(Number(it?.cantidad) > 0) ||
  it?.fob_unitario_usd === null ||
  it?.fob_unitario_usd === undefined;

// Gemelo viajero de buildAnexoPdfInput (Desarrollos), con tres correcciones
// propias del modelo viajero:
//   · condition sale de is_new (Desarrollos lo fija en NUEVO porque no tiene
//     la bandera; viajero sí la tiene).
//   · tax_amount = CIP UNITARIO × cantidad × tasa (Desarrollos guarda el CIP
//     por línea; viajero lo guarda por unidad).
//   · label cumple el contrato "NOMBRE - PK-NN" que el renderer parte en el
//     último " - " para las columnas de nombre y No. De PK.
// Función pura: sin red, sin efectos.
export function buildViajeAnexoPdfInput(viaje, perfil) {
  const sortedPackages = (viaje?.paquetes || []).map((pkg) => ({
    _seq: (pkg.sort_order ?? 0) + 1,
    label: `${(pkg.nombre || 'PAQUETE').toUpperCase()} - PK-${String((pkg.sort_order ?? 0) + 1).padStart(2, '0')}`,
    taxes: { dai: 0 },
    items: (pkg.items || []).map((it) => ({
      description: descripcionEfectiva(it),
      quantity: Number(it.cantidad) || 1,
      unit_value: Number(it.fob_unitario_usd) || 0,
      condition: it.is_new ? 'NUEVO' : 'USADO',
      tax_amount:
        (Number(it.cip_unitario_usd) || 0) * (Number(it.cantidad) || 1) * TASA_IMPUESTO_ANEXO,
    })),
  }));
  return {
    draft: { confirmed_at: viaje?.closed_at || nowIso() },
    config: {
      gestor_code: perfil?.codigo_gestor || '',
      aduana_code: perfil?.codigo_aduana || '',
      gestor_name: perfil?.nombre_gestor || '',
      registro_prefix: perfil?.no_registro || '',
    },
    sortedPackages,
  };
}

export async function generarAnexoPdf(viajeId) {
  return guard('generarAnexoPdf', 'No se pudo generar el anexo PDF.', async () => {
    const [viaje, perfil] = await Promise.all([getViaje(viajeId), getPerfilAduanero()]);

    // Un perfil sin los códigos DGA nunca imprime: mismo gate que deriva
    // savePerfilAduanero al calcular estado.
    if (perfil.estado !== 'activado') {
      return {
        ok: false,
        error: 'Completá el perfil aduanero (gestor y aduana) antes de generar el Anexo.',
      };
    }

    const incompletos = (viaje.paquetes || [])
      .filter((p) => (p.items || []).some(itemIncompleto))
      .map((p) => p.nombre || `Paquete ${(p.sort_order ?? 0) + 1}`);
    if (incompletos.length > 0) {
      return { ok: false, error: 'Hay artículos incompletos.', incompletos };
    }

    // drawTaxColumn apagado: convención casillero (las celdas IVA quedan en
    // blanco por fila), igual que los Anexos que Maxi/Lazo ya producen.
    const bytes = await renderAnexoPdf(buildViajeAnexoPdfInput(viaje, perfil), {
      drawTaxColumn: false,
      logLabel: `viajero:${viajeId}`,
    });

    return {
      ok: true,
      filename: `anexo-${slugRef(viaje.trip_reference)}-${nowIso().slice(0, 10)}.pdf`,
      bytes,
    };
  });
}

// ── Mock-surface compatibility ────────────────────────────────────────────────

// The mock exposed these for hermetic tests. They have no meaning against a real
// backend, but they are kept so `import * as api` has the same surface and any
// stray reference stays a no-op instead of a TypeError.
export const _mockConfig = { minDelay: 0, maxDelay: 0, failNext: null };

export function resetMockData() {
  tenantPromise = null;
}
